/**
 * Fixed banner shown while demo mode is active.
 *
 * The role switcher matters more than it looks: the dashboard renders four
 * different views (admin, employee, business, client) and without a way to
 * switch, a visitor only ever sees whichever one they logged into.
 */

import { useSyncExternalStore } from "react";

import {
  exitDemoMode,
  getDemoRole,
  mintDemoToken,
  setDemoRole,
  subscribeToDemoRole,
  type DemoRole,
} from "./session";
import { stashSeasonForRoleSwitch } from "./store";
import "./DemoBanner.css";

const ROLE_LABELS: { role: DemoRole; label: string }[] = [
  { role: "admin", label: "Admin" },
  { role: "employee", label: "Employee" },
  { role: "business", label: "Business" },
  { role: "client", label: "CSA member" },
];

export default function DemoBanner() {
  // Subscribed rather than read once: this component mounts at app boot, long
  // before /demo turns demo mode on.
  const role = useSyncExternalStore(subscribeToDemoRole, getDemoRole);

  if (!role) return null;

  const switchRole = (next: DemoRole) => {
    if (next === role) return;
    setDemoRole(next);
    localStorage.setItem("token", mintDemoToken(next));
    // Season settings survive the switch, so closing the business as admin is
    // visible from the business role. Everything else re-seeds.
    stashSeasonForRoleSwitch();
    // Full reload so every context provider refetches under the new role.
    window.location.assign("/dashboard");
  };

  const leave = () => {
    exitDemoMode();
    window.location.assign("/");
  };

  return (
    <div className="demo-banner" role="status">
      <span className="demo-banner__label">
        <span className="demo-banner__dot" aria-hidden="true" />
        Demo mode — sample data, nothing is saved
      </span>

      <div className="demo-banner__roles">
        {ROLE_LABELS.map(({ role: value, label }) => (
          <button
            key={value}
            type="button"
            className={`demo-banner__role${
              value === role ? " demo-banner__role--active" : ""
            }`}
            aria-pressed={value === role}
            onClick={() => switchRole(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <button type="button" className="demo-banner__exit" onClick={leave}>
        Exit demo
      </button>
    </div>
  );
}
